import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as liftsActions from './lifts.actions';
import { Lift } from '@models/lift.model';
import { Tray } from '@models/tray.model';
import { Slot } from '@models/slot.model';
import { Group } from '@app/tool/model/group.model';
import { Batch } from '@models/batch.model';
import { Plant } from '@models/plant.model';
import { PagedResponse } from "@models/response.model";
import { Line } from "@models/line.model";

export const liftsFeatureKey = 'lifts';

export interface LiftsState {
    lifts: PagedResponse<Lift>;
    tools: Group[];
    plants: Plant[];
    lines: Line[],
    createdLift: null;
    deletedLift: null;
    deletedSlot: Slot;
    updatedSlot: Slot;
    selectedLift: Lift;
    selectedTray: Tray;
    updatedTray: Tray;
    selectedSlot: Slot;
    selectedBatch: Batch;
    deletedBatch: Batch;
    deletedTray: Tray;
    addedTray: null;
    addedSlot: null;
    updatedBatch: Batch;
    createdBatch: Batch;
    isLoading: boolean;
    isLoadingLifts: boolean;
    isLoadingLift: boolean;
    isLoadingTools: boolean;
    error: any;
}

export const initialLiftState: LiftsState = {
    lifts: new PagedResponse<Lift>(),
    tools: [],
    plants: [],
    lines: [],
    createdLift: null,
    deletedLift: null,
    deletedSlot: null,
    updatedSlot: null,
    selectedLift: null,
    selectedTray: null,
    updatedTray: null,
    selectedSlot: null,
    selectedBatch: null,
    deletedBatch: null,
    deletedTray: null,
    addedTray: null,
    addedSlot: null,
    updatedBatch: null,
    createdBatch: null,
    isLoading: false,
    isLoadingLifts: false,
    isLoadingLift: false,
    isLoadingTools: false,
    error: null
};


export function reducer(state = initialLiftState, action: liftsActions.Action) {
    switch (action.type) {
        // Load Lifts
        case liftsActions.LOAD_LIFTS:
            return {
                ...state,
                isLoadingLifts: true,
                isLoading: true
            };
        case liftsActions.LOAD_LIFTS_SUCCESS:
            const loadedLifts = {
                items: action.firstPage ? action.payload.items : state.lifts.items.concat(action.payload.items),
                total: action.payload.total
            };
            return {
                ...state,
                lifts: loadedLifts,
                isLoadingLifts: false,
                isLoading: false
            };
        case liftsActions.LOAD_LIFTS_FAILED:
            return {
                ...state,
                isLoadingLifts: false,
                isLoading: false,
                error: action.payload
            };

        // Load Lift
        case liftsActions.LOAD_LIFT:
            return {
                ...state,
                isLoadingLift: true,
                isLoading: true
            };
        case liftsActions.LOAD_LIFT_SUCCESS:
            return {
                ...state,
                selectedLift: action.payload,
                isLoadingLift: false,
                selectedTray: null,
                updatedTray: null,
                selectedSlot: null,
                selectedBatch: null,
                updatedBatch: null,
                deletedBatch: null,
                isLoading: false
            };
        case liftsActions.LOAD_LIFT_FAILED:
            return {
                ...state,
                isLoadingLift: false,
                isLoading: false,
                selectedTray: null,
                updatedTray: null,
                selectedSlot: null,
                selectedBatch: null,
                updatedBatch: null,
                deletedBatch: null,
                error: action.error
            };

        // Load Plants
        case liftsActions.LOAD_PLANTS:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.LOAD_PLANTS_SUCCESS:
            return {
                ...state,
                plants: action.payload,
                isLoading: false
            };
        case liftsActions.LOAD_PLANTS_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.error
            };

        // Load Lines
        case liftsActions.LOAD_LINES:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.LOAD_LINES_SUCCESS:
            return {
                ...state,
                lines: action.payload,
                isLoading: false
            };
        case liftsActions.LOAD_LINES_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.error
            };

        // Add Lift
        case liftsActions.ADD_LIFT:
            return {
                ...state,
                createdLift: null,
                isLoading: true
            };
        case liftsActions.ADD_LIFT_SUCCESS:
            const liftsAfterAdded = {
                items: state.lifts.items.concat(action.payload),
                total: state.lifts.total + 1
            };

            return {
                ...state,
                createdLift: action.payload,
                lifts: liftsAfterAdded,
                isLoading: false
            };
        case liftsActions.ADD_LIFT_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Delete Lift
        case liftsActions.DELETE_LIFT:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.DELETE_LIFT_SUCCESS:
            const liftsAfterDeleted = {
                items: state.lifts.items.filter(item => item.id !== action.payload.id),
                total: state.lifts.total - 1
            };
            return {
                ...state,
                deletedLift: action.payload,
                // tslint:disable-next-line:no-shadowed-variable
                lifts: liftsAfterDeleted,
                isLoading: false
            };
        case liftsActions.DELETE_LIFT_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Edit Lift Name
        case liftsActions.RENAME_LIFT:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.RENAME_LIFT_SUCCESS:
            const renamedLift = state.selectedLift;
            const newLifts = state.lifts.items.map(item => {
                if (item.id === renamedLift.id) {
                    // Return a new object for the updated item
                    return {
                        ...item,
                        name: renamedLift.name
                    };
                }
                // Return the original item if it doesn't match
                return item;
            });
            const liftsAfterRename = {
                items: newLifts,
                total: state.lifts.total
            };
            return {
                ...state,
                selectedLift: action.payload,
                lifts: liftsAfterRename,
                isLoading: false
            };
        case liftsActions.RENAME_LIFT_FAILED:
            return {
                ...state,
                isLoading: false,

                error: action.payload
            };

        // Add Tray
        case liftsActions.ADD_TRAY:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.ADD_TRAY_SUCCESS:
            const newLift = {
                ...state.selectedLift,
                trays: [...state.selectedLift.trays, action.payload]
            };
            return {
                ...state,
                selectedLift: newLift,
                isLoading: false
            };
        case liftsActions.ADD_TRAY_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Delete tray
        case liftsActions.DELETE_TRAY:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.DELETE_TRAY_SUCCESS:
            const liftWithoutTray = {...state.selectedLift};
            liftWithoutTray.trays = liftWithoutTray.trays.filter(tray => tray.id !== action.payload.id)
            return {
                ...state,
                deletedTray: action.payload,
                selectedLift: liftWithoutTray,
                isLoading: false
            };
        case liftsActions.DELETE_TRAY_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Change Tray state tray
        case liftsActions.TOGGLE_TRAY_STATE:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.TOGGLE_TRAY_STATE_SUCCESS:
            const liftForTrays = {...state.selectedLift};
            const newTrays = liftForTrays.trays.map(tray => {
                if (tray.id === action.payload.id) {
                    return {...tray, disabled: action.payload.disabled} as Tray;
                }
                return tray
            })
            liftForTrays.trays = newTrays;
            return {
                ...state,
                selectedLift: liftForTrays,
                isLoading: false
            };
        case liftsActions.TOGGLE_TRAY_STATE_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Load tray details
        case liftsActions.LOAD_TRAY_DETAILS:
            return {
                ...state,
                selectedTray: null,
                selectedSlot: null,
                selectedBatch: null,
                isLoading: true
            };
        case liftsActions.LOAD_TRAY_DETAILS_SUCCESS:
            return {
                ...state,
                selectedTray: action.payload,
                updatedTray: null,
                selectedSlot: null,
                selectedBatch: null,
                updatedBatch: null,
                deletedBatch: null,
                isLoading: false
            };
        case liftsActions.LOAD_TRAY_DETAILS_FAILED:
            return {
                ...state,
                isLoading: false,
                selectedSlot: null,
                selectedBatch: null,
                updatedBatch: null,
                deletedBatch: null,
                error: action.payload
            };

        // Update tray
        case liftsActions.UPDATE_TRAY:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.UPDATE_TRAY_SUCCESS:
            return {
                ...state,
                selectedTray: action.payload,
                updatedTray: action.payload,
                isLoading: false
            };
        case liftsActions.UPDATE_TRAY_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Load slot details
        case liftsActions.LOAD_SLOT:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.LOAD_SLOT_SUCCESS:
            return {
                ...state,
                selectedSlot: action.payload,
                selectedBatch: null,
                updatedBatch: null,
                deletedBatch: null,
                isLoading: false
            };
        case liftsActions.LOAD_SLOT_FAILED:
            return {
                ...state,
                isLoading: false,
                selectedBatch: null,
                updatedBatch: null,
                deletedBatch: null,
                error: action.payload
            };

        // Rename slot
        case liftsActions.RENAME_SLOT:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.RENAME_SLOT_SUCCESS:
            const newTray = {
                ...state.selectedTray, slots: state.selectedTray.slots.map(slot => {
                    const slotCopyBecauseReasons = {...slot}
                    if (slotCopyBecauseReasons.id === action.payload.id) {
                        slotCopyBecauseReasons.name = action.payload.name;
                    }
                    return slotCopyBecauseReasons;
                })
            };
            return {
                ...state,
                updatedSlot: action.payload,
                selectedTray: newTray,
                isLoading: false
            };
        case liftsActions.RENAME_SLOT_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };


        // Add Slot
        case liftsActions.ADD_SLOT:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.ADD_SLOT_SUCCESS:
            const tray1 = {...state.selectedTray};
            const addedSlot = new Slot(action.payload);
            const newSlot = tray1.slots.length === 0 ? addedSlot : state.selectedSlot;

            return {
                ...state,
                addedSlot: addedSlot,
                selectedTray: {
                    ...state.selectedTray,
                    slots: [...tray1.slots, addedSlot]
                },
                selectedSlot: newSlot,
                isLoading: false
            };
        case liftsActions.ADD_SLOT_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Duplicate Slot
        case liftsActions.DUPLICATE_SLOT:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.LOAD_DUPLICATED_SLOT:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.LOAD_DUPLICATED_SLOT_SUCCESS:
            const addedSlotD = new Slot(action.payload);
            const trayD = {...state.selectedTray, slots: [...state.selectedTray.slots, addedSlotD]};

            return {
                ...state,
                addedSlot: addedSlotD,
                selectedTray: trayD,
                selectedSlot: addedSlotD,
                isLoading: false
            };
        case liftsActions.DUPLICATE_SLOT_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Copy Slot
        case liftsActions.COPY_SLOT:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.LOAD_COPIED_SLOT:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.LOAD_COPIED_SLOT_SUCCESS:
            const trayC = state.selectedTray;
            const addedSlotC = new Slot(action.payload);
            trayC.slots.push(addedSlotC)

            return {
                ...state,
                addedSlot: addedSlotC,
                selectedTray: trayC,
                selectedSlot: addedSlotC,
                isLoading: false
            };
        case liftsActions.COPY_SLOT_SUCCESS:
            return {
                ...state,
                isLoading: false,
            };
        case liftsActions.COPY_SLOT_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Delete slot
        case liftsActions.DELETE_SLOT:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.DELETE_SLOT_SUCCESS:
            const tray2 = {...state.selectedTray};
            tray2.slots = tray2.slots.filter(slot => slot.id !== action.payload.id)
            const newSelectedSlot = action.payload.id === state.selectedSlot.id ? tray2.slots[0] : state.selectedSlot;

            return {
                ...state,
                deletedSlot: action.payload,
                selectedTray: tray2,
                selectedSlot: newSelectedSlot,
                isLoading: false
            };
        case liftsActions.DELETE_SLOT_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Load tools
        case liftsActions.LOAD_ALL_TOOLS:
            return {
                ...state,
                isLoadingTools: true
            };

        case liftsActions.LOAD_ALL_TOOLS_BY_SLOT:
            return {
                ...state,
                isLoadingTools: true
            };
        case liftsActions.LOAD_ALL_TOOLS_SUCCESS:
            return {
                ...state,
                selectedBatch: null,
                updatedBatch: null,
                deletedBatch: null,
                tools: action.payload,
                isLoadingTools: false
            };
        case liftsActions.LOAD_ALL_TOOLS_FAILED:
            return {
                ...state,
                isLoadingTools: false,
                error: action.payload
            };

        // Load batch
        case liftsActions.LOAD_BATCH:
            return {
                ...state,
                isLoading: true
            };

        case liftsActions.LOAD_BATCH_SUCCESS:
            return {
                ...state,
                selectedBatch: action.batch,
                isLoading: false
            };
        case liftsActions.LOAD_BATCH_FAILED:
            return {
                ...state,
                error: action.payload,
                isLoading: false
            };

        // Copy batch
        case liftsActions.COPY_BATCH:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.COPY_BATCH_SUCCESS:
            return {
                ...state,
                isLoading: false
            };
        case liftsActions.COPY_BATCH_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Duplicate batch
        case liftsActions.DUPLICATE_BATCH:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.DUPLICATE_BATCH_SUCCESS:
            return {
                ...state,
                createdBatch: action.batch,
                selectedSlot: {
                    ...state.selectedSlot,
                    batches: [...state.selectedSlot.batches, action.batch]
                },
                isLoading: false
            };
        case liftsActions.DUPLICATE_BATCH_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Create batch with tools
        case liftsActions.CREATE_BATCH_WITH_TOOLS:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.CREATE_BATCH_WITH_TOOLS_SUCCESS:
            const slot = {
                ...state.selectedSlot,
                batches: [...state.selectedSlot.batches, action.payload]
            }

            return {
                ...state,
                createdBatch: slot,
                selectedSlot: slot,
                isLoading: false
            };
        case liftsActions.CREATE_BATCH_WITH_TOOLS_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Delete batch
        case liftsActions.DELETE_BATCH:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.DELETE_BATCH_SUCCESS:
            const updatableSlot = {...state.selectedSlot};
            updatableSlot.batches = updatableSlot.batches.filter(batch => batch.id !== action.payload.id)
            const newSelectedBatch = state.selectedBatch &&
            action.payload.id === state.selectedBatch.id ? null : state.selectedBatch;

            return {
                ...state,
                deletedBatch: action.payload,
                selectedBatch: newSelectedBatch,
                updatedBatch: null,
                selectedSlot: updatableSlot,
                isLoading: false
            };
        case liftsActions.DELETE_BATCH_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Add tools to batch
        case liftsActions.ADD_TOOLS_TO_BATCH:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.ADD_TOOLS_TO_BATCH_SUCCESS:
            return {
                ...state,
                updatedBatch: action.payload,
                isLoading: false
            };
        case liftsActions.ADD_TOOLS_TO_BATCH_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Remove tools to batch
        case liftsActions.REMOVE_TOOLS_FROM_BATCH:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.REMOVE_TOOLS_FROM_BATCH_SUCCESS:
            return {
                ...state,
                updatedBatch: action.payload,
                isLoading: false
            };
        case liftsActions.REMOVE_TOOLS_FROM_BATCH_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Rename cart for tool
        case liftsActions.UPDATE_CART_BATCH_TOOLS:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.UPDATE_CART_BATCH_TOOLS_SUCCESS:
            return {
                ...state,
                updatedBatch: action.payload,
                isLoading: false
            };
        case liftsActions.UPDATE_CART_BATCH_TOOLS_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        // Update batch
        case liftsActions.UPDATE_BATCH:
            return {
                ...state,
                isLoading: true
            };
        case liftsActions.UPDATE_BATCH_SUCCESS:
            const batchIndex = state.selectedSlot.batches.findIndex(batch => batch.id === action.payload.id);
            const newBatches = [...state.selectedSlot.batches];
            newBatches[batchIndex] = action.payload;
            const newishSlot = {
                ...state.selectedSlot,
                batches: newBatches
            };
            const newBatch = state.selectedBatch &&
            action.payload.id === state.selectedBatch.id ? action.payload : state.selectedBatch;

            return {
                ...state,
                selectedBatch: newBatch,
                updatedBatch: action.payload,
                selectedSlot: newishSlot,
                isLoading: false
            };
        case liftsActions.UPDATE_BATCH_FAILED:
            return {
                ...state,
                isLoading: false,
                error: action.payload
            };

        default:
            return state;
    }
}

export const getLiftsState = createFeatureSelector<LiftsState>(liftsFeatureKey);
export const getLifts = createSelector(getLiftsState, (state: LiftsState) => state.lifts);
export const getPlants = createSelector(getLiftsState, (state: LiftsState) => state.plants);
export const getLines = createSelector(getLiftsState, (state: LiftsState) => state.lines);
export const getLift = createSelector(getLiftsState, (state: LiftsState) => state.selectedLift);
export const getDeletedLift = createSelector(getLiftsState, (state: LiftsState) => state.deletedLift);
export const getDeletedTray = createSelector(getLiftsState, (state: LiftsState) => state.deletedTray);
export const getTray = createSelector(getLiftsState, (state: LiftsState) => state.selectedTray);
export const getUpdatedTray = createSelector(getLiftsState, (state: LiftsState) => state.updatedTray);
export const getSlot = createSelector(getLiftsState, (state: LiftsState) => state.selectedSlot);
export const getDeletedSlot = createSelector(getLiftsState, (state: LiftsState) => state.deletedSlot);
export const getUpdatedSlot = createSelector(getLiftsState, (state: LiftsState) => state.updatedSlot);
export const getAddedSlot = createSelector(getLiftsState, (state: LiftsState) => state.addedSlot);
export const getTools = createSelector(getLiftsState, (state: LiftsState) => state.tools);
export const getBatch = createSelector(getLiftsState, (state: LiftsState) => state.selectedBatch);
export const getCreatedBatch = createSelector(getLiftsState, (state: LiftsState) => state.createdBatch);
export const getUpdatedBatch = createSelector(getLiftsState, (state: LiftsState) => state.updatedBatch);
export const getDeletedBatch = createSelector(getLiftsState, (state: LiftsState) => state.deletedBatch);
export const isLoading = createSelector(getLiftsState, (state: LiftsState) => state.isLoading);
export const isLoadingLifts = createSelector(getLiftsState, (state: LiftsState) => state.isLoadingLifts);
export const isLoadingLift = createSelector(getLiftsState, (state: LiftsState) => state.isLoadingLift);
export const isLoadingTools = createSelector(getLiftsState, (state: LiftsState) => state.isLoadingTools);
export const getError = createSelector(getLiftsState, (state: LiftsState) => state.error);
